import { useState, useEffect } from "react";
import { TrainingAPI } from "../../../api/TrainingAPI";
import { Space } from "antd";
import { audioMessage as message } from "../../Common/Notifications/audioMessage";
import { useDispatch, useSelector } from "react-redux";
import { getColorStrategies, updateDifficultyBarConfig, getTeamInfo } from "../../../store/actions/team.actions";
import { displayValidationErrorMessages } from "../../Settings/TeamSettings";
import BlurredCustomColorStrategies from "./blurred_custom_color_strategy.png";
import { PremiumFeatureDisabled } from "../../Common/PremiumFeatureDisabled";
import { DifficultyBarConfig } from "./DifficultyBarConfig";
import { ColorStrategyModal } from "./ColorStrategyModal";
import { ColorStrategyManagement } from "./ColorStrategyManagement";
import { AdminNotice } from "../AdminNotice";
import { useAuth0 } from "@auth0/auth0-react";
import "../../../Styles/styles-CustomColorStrategies.css"

export const CustomColorStrategies = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingStrategy, setEditingStrategy] = useState(null);
  const trainingAPI = new TrainingAPI();
  const dispatch = useDispatch();

  const {
    color_strategies: strategies,
    info: { custom_colors_enabled }
  } = useSelector(({ team }) => team);

  const { user } = useAuth0();
  const isStaffEmail = user?.email?.endsWith("gpsdataviz.com");

  useEffect(() => {
    dispatch(getColorStrategies());
    dispatch(getTeamInfo());
  }, [dispatch]);

  const handleModalSubmit = async (strategyData, editingStrategy) => {
    try {
      if (editingStrategy) {
        await trainingAPI.updateColorStrategy({
          ...strategyData,
          strategy_id: editingStrategy.id
        });
        message.success("Strategy updated successfully");
      } else {
        await trainingAPI.createColorStrategy(strategyData);
        message.success("Strategy created successfully");
      }
      setIsModalVisible(false);
      setEditingStrategy(null);
      dispatch(getColorStrategies());
    } catch (error) {
      message.error(typeof error === 'string' ? error : "Error saving color strategy");
    }

    validateTeam();
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    setEditingStrategy(null);
  };

  const handleDelete = async (selectedStrategy) => {
    try {
      await trainingAPI.deleteColorStrategy(selectedStrategy.id);
      message.success("Strategy deleted successfully");
      dispatch(getColorStrategies());
    } catch (error) {
      message.error(typeof error === 'string' ? error : "Error deleting color strategy");
    }

    validateTeam();
  };

  const handleEdit = (selectedStrategy) => {
    setEditingStrategy(selectedStrategy);
    setIsModalVisible(true);
  };

  const handleAdd = () => {
    setEditingStrategy(null);
    setIsModalVisible(true);
  };

  const handleDifficultyBarSave = async (config) => {
    try {
      await dispatch(updateDifficultyBarConfig({ difficulty_bar_config: config }));
      message.success("Difficulty bar configuration saved successfully");
      validateTeam();
    } catch (error) {
      message.error(typeof error === 'string' ? error : "Error saving difficulty bar configuration");
    }
  };

  const validateTeam = async () => {
    try {
      const errorMessages = await trainingAPI.validateTeam();
      if(errorMessages && Array.isArray(errorMessages)) {
        displayValidationErrorMessages(errorMessages)
      }
    } catch (error) {
      message.error("Error performing team validation");
    }
  };

  if (!custom_colors_enabled && !isStaffEmail) {
    return (
      <PremiumFeatureDisabled
        bg_image={BlurredCustomColorStrategies}
        show_navbar={false}
        feature_name={"Custom Coloring"}
        backgroundPosition={'unset'}
        info_text={<>
        This is a premium feature that will allow you to customize the colors of your GPS reports. <br></br>
        If you are interested in adding this to your platform, please contact your sales rep!</>}
      />
    )
  }

  return (
    <div style={{ maxWidth: 800 }}>
      <Space direction="vertical" style={{ width: "100%" }} size="large">
        {
          (!custom_colors_enabled && isStaffEmail) ? 
            <AdminNotice />
           : null
        }
        {/* Color Strategy Management Section */}
        <ColorStrategyManagement
          strategies={strategies}
          onAdd={handleAdd}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />

        {/* Difficulty Bar Configuration Section */}
        <DifficultyBarConfig
          onSave={handleDifficultyBarSave}
        />

        <ColorStrategyModal
          isVisible={isModalVisible}
          onCancel={handleModalCancel}
          onSubmit={handleModalSubmit}
          editingStrategy={editingStrategy}
        />
      </Space>
    </div>
  );
}; 